import { pipe } from 'fp-ts/function';
import { find, map } from 'lodash/fp';
import { change } from 'redux-form';
import { useDispatch } from 'react-redux';
import { Grid, Stack } from '@optimus-web/core';
import { DurationService, LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { useCommanderSelector } from '@mews-commander/utils';
import {
    commanderForm,
    InputField,
    presence,
    SelectField,
    useFormValueSelector,
    validation,
} from '@mews-commander/legacy-form';
import { getEnterprises } from '@mews-commander/legacy-entities/src/ducks/enterprises';
import { EXTEND_EHW_PAGE_FORM } from './constants';

const ExtendEHWPageForm = () => {
    const enterprises = useCommanderSelector(getEnterprises);
    const enterpriseOptions = map(({ id, name }) => ({ value: id, name, label: name }), enterprises);
    const dispatch = useDispatch();
    const isEnterpriseSelected = useFormValueSelector(EXTEND_EHW_PAGE_FORM, 'enterpriseId');

    const prefillEHW = (historyWindow: string, daysFieldName: string, hoursFieldName: string) => {
        const duration = DurationService.parse(historyWindow);
        const hours = DurationService.asHours(duration);
        const days = Math.floor(hours / 24);
        const remainingHours = hours % 24;

        dispatch(change(EXTEND_EHW_PAGE_FORM, daysFieldName, days.toString()));
        dispatch(change(EXTEND_EHW_PAGE_FORM, hoursFieldName, remainingHours.toString()));
    };

    const onEnterpriseSelect = (enterpriseId: string) => {
        const enterprise = find(({ id }) => id === enterpriseId, enterprises);

        if (enterprise) {
            prefillEHW(enterprise.accountingEditableHistoryWindow, 'aehwDays', 'aehwHours');
            prefillEHW(enterprise.operationalEditableHistoryWindow, 'oehwDays', 'oehwHours');
        }
    };

    return (
        <Stack vertical spacing="300">
            <SelectField
                name="enterpriseId"
                form={EXTEND_EHW_PAGE_FORM}
                options={enterpriseOptions}
                showEmptyValue
                onChange={onEnterpriseSelect}
                size="medium"
                label={<LocalizedText textKey={TextKey.Enterprise} />}
            />
            {isEnterpriseSelected ? (
                <>
                    <Grid templateColumns="1fr 1fr" alignItems="center">
                        <InputField
                            name="aehwDays"
                            label={<LocalizedText textKey={TextKey.AccountingEditableHistoryWindow} />}
                            form={EXTEND_EHW_PAGE_FORM}
                            size="medium"
                            suffix={<LocalizedText textKey={TextKey.Days} />}
                        />
                        <InputField
                            name="aehwHours"
                            helper={<LocalizedText textKey={TextKey.AccountingEditableHistoryWindowHint} />}
                            form={EXTEND_EHW_PAGE_FORM}
                            size="medium"
                            suffix={<LocalizedText textKey={TextKey.Hours} />}
                        />
                    </Grid>
                    <Grid templateColumns="1fr 1fr" alignItems="center">
                        <InputField
                            name="oehwDays"
                            label={<LocalizedText textKey={TextKey.OperationalEditableHistoryWindow} />}
                            form={EXTEND_EHW_PAGE_FORM}
                            size="medium"
                            suffix={<LocalizedText textKey={TextKey.Days} />}
                        />
                        <InputField
                            name="oehwHours"
                            helper={<LocalizedText textKey={TextKey.OperationalEditableHistoryWindowHint} />}
                            form={EXTEND_EHW_PAGE_FORM}
                            size="medium"
                            suffix={<LocalizedText textKey={TextKey.Hours} />}
                        />
                    </Grid>
                </>
            ) : null}
        </Stack>
    );
};

const fieldConstraint = {
    numericality: {
        message: TextKey.ValidateNumber,
        onlyInteger: true,
    },
    presence,
};

const constraints = {
    aehwDays: fieldConstraint,
    oehwDays: fieldConstraint,
    aehwHours: fieldConstraint,
    oehwHours: fieldConstraint,
};

export default pipe(
    ExtendEHWPageForm,
    commanderForm({
        submitLabel: <LocalizedText textKey={TextKey.Save} />,
        form: EXTEND_EHW_PAGE_FORM,
        validate: validation(constraints),
    })
);
